import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import { getInvoiceAuditTrail } from "api/invoice.api";

// ── Action label config ───────────────────────────────────────────────────────
const ACTION_CONFIG = {
  CREATE_INVOICE: {
    label: "Draft Created",
    icon: "FilePlus",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  },
  ISSUE_INVOICE: {
    label: "Invoice Issued",
    icon: "Send",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  },
  MANUAL_RETRY_REPORTING: {
    label: "Manual Retry Triggered",
    icon: "RefreshCw",
    color:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  },
  ZATCA_REPORTED: {
    label: "Reported to ZATCA",
    icon: "CheckCircle2",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
  ZATCA_CLEARED: {
    label: "Cleared by ZATCA",
    icon: "Shield",
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  },
  ZATCA_REJECTED: {
    label: "Rejected by ZATCA",
    icon: "XCircle",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  },
};

const STATUS_COLORS = {
  SUCCESS:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  PENDING:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  ERROR: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

// ── Component ─────────────────────────────────────────────────────────────────
const AuditTrailModal = ({ invoice, onClose }) => {
  const [auditTrail, setAuditTrail] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!invoice?.id) return;

    setIsLoading(true);
    setError(null);

    getInvoiceAuditTrail(invoice.id)
      .then((data) => {
        setAuditTrail(data.auditTrail || []);
      })
      .catch((err) => {
        setError(err.message || "Failed to load audit trail");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [invoice?.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Audit Trail
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Invoice {invoice.invoiceNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="AlertTriangle"
                size={40}
                className="text-red-500 mb-3"
              />
              <p className="text-foreground font-medium mb-1">
                Failed to load audit trail
              </p>
              <p className="text-muted-foreground text-sm">{error}</p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && auditTrail.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="FileText"
                size={40}
                className="text-muted-foreground mb-3"
              />
              <p className="text-foreground font-medium mb-1">
                No audit events found
              </p>
              <p className="text-muted-foreground text-sm">
                No activity has been recorded for this invoice yet.
              </p>
            </div>
          )}

          {/* Timeline */}
          {!isLoading && !error && auditTrail.length > 0 && (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-border" />

              <div className="space-y-6">
                {auditTrail.map((event, index) => {
                  const config = ACTION_CONFIG[event.action] || {
                    label: event.action.replace(/_/g, " "),
                    icon: "Activity",
                    color: "bg-gray-100 text-gray-600",
                  };
                  const statusColor =
                    STATUS_COLORS[event.status] || "bg-gray-100 text-gray-600";

                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative flex gap-4 pl-12"
                    >
                      {/* Icon bubble */}
                      <div
                        className={`absolute left-0 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color} border-2 border-white dark:border-gray-800 shadow-sm z-10`}
                      >
                        <Icon name={config.icon} size={16} />
                      </div>

                      {/* Event card */}
                      <div className="flex-1 bg-muted/30 dark:bg-gray-700/30 rounded-xl p-4 border border-border">
                        {/* Top row */}
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-foreground text-sm">
                              {config.label}
                            </span>
                            {event.status && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}
                              >
                                {event.status}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                            {new Date(event.timestamp).toLocaleString("en-SA", {
                              dateStyle: "medium",
                              timeStyle: "short",
                            })}
                          </span>
                        </div>

                        {/* Performed by */}
                        <p className="text-xs text-muted-foreground mb-3">
                          By {event.performedBy}
                        </p>

                        {/* Details */}
                        {event.details && (
                          <div className="space-y-1">
                            {event.details.invoiceNumber &&
                              event.details.invoiceNumber !== "DRAFT" && (
                                <p className="text-xs text-foreground">
                                  Invoice No:{" "}
                                  <span className="font-medium font-mono">
                                    {event.details.invoiceNumber}
                                  </span>
                                </p>
                              )}
                            {event.details.processingTime && (
                              <p className="text-xs text-muted-foreground">
                                Processing time: {event.details.processingTime}
                                ms
                              </p>
                            )}
                            {event.details.previousAttempts !== undefined && (
                              <p className="text-xs text-muted-foreground">
                                Previous attempts:{" "}
                                {event.details.previousAttempts}
                              </p>
                            )}
                            {event.details.error && (
                              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                <p className="text-xs text-red-600 dark:text-red-400">
                                  {event.details.error}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ZATCA Response */}
                        {event.zatcaResponse && (
                          <div className="mt-3 p-2 bg-muted dark:bg-gray-700 rounded-lg">
                            <p className="text-xs font-medium text-foreground mb-1">
                              ZATCA Response
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Status: {event.zatcaResponse.status}
                              {event.zatcaResponse.message &&
                                ` — ${event.zatcaResponse.message}`}
                            </p>
                          </div>
                        )}

                        {/* Invoice Hash */}
                        {event.invoiceHash && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">
                              Hash:{" "}
                              <span className="font-mono">
                                {event.invoiceHash.substring(0, 32)}...
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuditTrailModal;
